import { useEffect, useState } from "react";
import { Wind, Mail, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { releaseNote, sendNoteToSelf, getNoteCount } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export function BecomingRelease() {
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState(null); // null | "universe" | "self"
  const [sending, setSending] = useState(false);
  const [released, setReleased] = useState(null); // count after release
  const [count, setCount] = useState(null);

  useEffect(() => {
    getNoteCount().then((d) => setCount(d.released)).catch(() => {});
  }, []);

  const release = async () => {
    setSending(true);
    try {
      const d = await releaseNote(note);
      setReleased(d.released);
      setCount(d.released);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const toSelf = async () => {
    setSending(true);
    try {
      await sendNoteToSelf(note, email);
      setMode("sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setNote("");
    setEmail("");
    setMode(null);
    setReleased(null);
  };

  return (
    <section className="bg-royal-soft border border-royal/10" data-testid="becoming-release">
      <div className="mx-auto max-w-4xl px-6 md:px-10 py-16 md:py-24">
        <p className="eyebrow">Unburden yourself</p>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
          Take something, leave something
        </h2>
        <p className="mt-5 max-w-2xl text-neutral-600 leading-relaxed">
          The web version of the bottle and the soil. Write down a burden, a wish, or an encouraging
          thought. It is completely private. No one reads it, not even Seyoun.
        </p>

        <AnimatePresence mode="wait">
          {released !== null ? (
            <motion.div
              key="released"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-10 text-center py-10"
              data-testid="release-success"
            >
              <motion.div
                initial={{ opacity: 0.8, y: 0 }}
                animate={{ opacity: 0, y: -120 }}
                transition={{ duration: 2.2, ease: "easeOut" }}
                className="mx-auto max-w-md font-serif text-xl italic text-neutral-500"
              >
                “{note.length > 120 ? note.slice(0, 120) + "…" : note}”
              </motion.div>
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-royal">
                <Wind size={22} />
              </span>
              <h3 className="mt-4 font-serif text-3xl text-ink">Released.</h3>
              <p className="mt-2 text-sm text-neutral-600">
                The universe has it now. You are lighter.
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-neutral-400">
                {released} {released === 1 ? "note" : "notes"} released so far
              </p>
              <button
                type="button"
                data-testid="release-again-button"
                onClick={reset}
                className="mt-6 text-sm text-royal link-underline"
              >
                Write another
              </button>
            </motion.div>
          ) : mode === "sent" ? (
            <motion.div
              key="sent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-10 text-center py-10"
              data-testid="self-send-success"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-royal">
                <Check size={22} />
              </span>
              <h3 className="mt-4 font-serif text-3xl text-ink">It's on its way to you.</h3>
              <p className="mt-2 text-sm text-neutral-600 max-w-sm mx-auto">
                Your note was emailed only to you. It is not stored anywhere and no one else received it.
              </p>
              <button
                type="button"
                data-testid="self-send-again-button"
                onClick={reset}
                className="mt-6 text-sm text-royal link-underline"
              >
                Write another
              </button>
            </motion.div>
          ) : (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-10">
              <Label htmlFor="becoming-note" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                Your note
              </Label>
              <Textarea
                id="becoming-note"
                data-testid="becoming-note-input"
                rows={5}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="field resize-none bg-white/60"
                placeholder="A burden, a wish, a thought you're ready to set down…"
              />

              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div className="bg-white border border-neutral-200 p-6">
                  <p className="font-serif text-xl text-ink">Send it to the universe</p>
                  <p className="mt-2 text-sm text-neutral-500">
                    Let it go. It disappears the moment you release it. Never stored, never read.
                  </p>
                  <button
                    type="button"
                    data-testid="release-to-universe-button"
                    disabled={sending || !note.trim()}
                    onClick={release}
                    className="mt-5 w-full bg-royal text-white py-3.5 text-sm tracking-wide hover:bg-royal-deep transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    <Wind size={15} /> {sending ? "Releasing…" : "Release it"}
                  </button>
                </div>
                <div className="bg-white border border-neutral-200 p-6">
                  <p className="font-serif text-xl text-ink">Send it to yourself</p>
                  <p className="mt-2 text-sm text-neutral-500">
                    Keep it close. Your note arrives in your own inbox, addressed from you to you.
                  </p>
                  <Input
                    type="email"
                    data-testid="becoming-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="field mt-4"
                    placeholder="you@example.com"
                  />
                  <button
                    type="button"
                    data-testid="send-to-self-button"
                    disabled={sending || !note.trim() || !email.trim()}
                    onClick={toSelf}
                    className="mt-4 w-full border border-royal text-royal py-3.5 text-sm tracking-wide hover:bg-royal hover:text-white transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    <Mail size={15} /> {sending ? "Sending…" : "Email it to me"}
                  </button>
                </div>
              </div>
              {count !== null && count > 0 && (
                <p className="mt-6 text-center text-xs uppercase tracking-[0.2em] text-neutral-400" data-testid="release-count">
                  {count} {count === 1 ? "note has" : "notes have"} been released here
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
